import { COLLECTION_ID_FIELD_NAME } from "helpers/server-rollup";
import { formatMessage } from "lib/globalize-util";

// NU-13821: As a buyer, I can add Notes for reference to each cell within an assortment
// Note component made using only JavaScript vanilla
export class NoteComponent {
    isNewNoteOpened = false;
    dimensions = { width: 218, height: 92 };
    delay = { hide: 500, show: 500 };
    placeholder = formatMessage("Insert note here");
    maxLength = 1000;
    noteElement;
    params;

    private getCollectionId(data) {
        return data?.fields?.[COLLECTION_ID_FIELD_NAME]?.string_value;
    }

    // Method to build the component
    private buildNote() {
        const { column, context, data, eGridCell, node } = this.params;
        const itemId = data.fields.id.string_value;
        const collectionId = this.getCollectionId(data);
        const currentNoteValue = context.getNoteValue(itemId, column.colId, collectionId) || "";
        const textarea = document.createElement("textarea");
        const rect = eGridCell.getBoundingClientRect();
        // rightSideWidth = 32;
        const rightSideWidth = this.dimensions.width + 32;
        // bottomSideHeight = 50;
        const bottomSideHeight = this.dimensions.height + 50;

        textarea.className = "noteComponent";
        textarea.maxLength = this.maxLength;
        textarea.style.top = `${
            rect.bottom > window.innerHeight - bottomSideHeight ? rect.bottom - this.dimensions.height : rect.top
        }px`;
        textarea.style.left = `${
            rect.right > window.innerWidth - rightSideWidth ? rect.left - this.dimensions.width : rect.right
        }px`;
        textarea.placeholder = this.placeholder;
        document.querySelector(".assortmentBodyContent").appendChild(textarea);

        if (context.editable) {
            textarea.addEventListener("blur", this.handleNoteBlur);
            textarea.focus();
        } else {
            textarea.disabled = true;
        }

        textarea.value = currentNoteValue;

        return textarea;
    }

    // Method to handle when the user mouse out the current cell or note
    private handleMouseLeaveCellOrNote = () => {
        const { eGridCell } = this.params;

        if (!this.isNewNoteOpened) {
            setTimeout(() => {
                const noteComponent: any = document.querySelector(".noteComponent");

                if (
                    noteComponent &&
                    !document.querySelector(".noteComponent:hover") &&
                    !eGridCell.querySelector(":hover")
                ) {
                    noteComponent.blur();
                    noteComponent.remove();
                }
            }, this.delay.hide);
        }
    };

    // Method to handle when the user mouse over a cell
    private handleMouseMoveOnCell = () => {
        const { column, context, data } = this.params;
        const itemId = data.fields.id.string_value;
        const collectionId = this.getCollectionId(data);

        setTimeout(() => {
            const currentNoteValue = context.getNoteValue(itemId, column.colId, collectionId);

            if (!document.querySelector(".noteComponent") && currentNoteValue) {
                this.noteElement = this.buildNote();

                this.noteElement.addEventListener("mouseleave", this.handleMouseLeaveCellOrNote);
            }
        }, this.delay.show);
    };

    private handleNoteBlur = (event) => {
        const {
            eGridCell,
            column,
            data,
            node,
            context: { clearRequestedItemId, getNoteValue, onUpdateNote, editable },
        } = this.params;

        const newValue = (event.target as HTMLInputElement).value;
        const itemId = data.fields.id.string_value;
        const collectionId = this.getCollectionId(data);
        const currentNoteValue = getNoteValue(itemId, column.colId, collectionId) || "";

        if (newValue !== currentNoteValue && editable) {
            onUpdateNote(itemId, column.colId, newValue, node);
        }

        // Remove element
        this.noteElement.remove();
        this.noteElement = undefined;
        this.isNewNoteOpened = false;
        eGridCell.classList.remove("isNoteEditing");
        clearRequestedItemId();
    };

    // Method to attach mouse events o the existing cell that has a note
    private addEventsToExistingCellWithNote() {
        const { eGridCell } = this.params;

        eGridCell.addEventListener("mousemove", this.handleMouseMoveOnCell);
        eGridCell.addEventListener("mouseleave", this.handleMouseLeaveCellOrNote);
    }

    private removeEventListeners() {
        const { eGridCell } = this.params;

        if (eGridCell) {
            eGridCell.removeEventListener("mousemove", this.handleMouseMoveOnCell);
            eGridCell.removeEventListener("mouseleave", this.handleMouseLeaveCellOrNote);
        }

        if (this.noteElement) {
            this.noteElement.removeEventListener("blur", this.handleNoteBlur);
            this.noteElement.removeEventListener("mouseleave", this.handleMouseLeaveCellOrNote);
        }
    }

    // Initial method to setup the component
    public init(params) {
        this.params = params;
        const { data, column, eGridCell, context } = this.params;
        const itemId = data?.fields?.id?.string_value;
        const collectionId = this.getCollectionId(data);
        const { getNoteValue, getRequestedItemId, getRequestedColumnId } = context;
        const currentNoteValue = itemId && getNoteValue?.(itemId, column.colId, collectionId);

        if (currentNoteValue) {
            // Attach mouse events to existing cell with note
            this.addEventsToExistingCellWithNote();
        } else if (itemId && getRequestedItemId?.() === itemId && getRequestedColumnId() === column.colId) {
            // Create new temporal note component
            this.noteElement = this.buildNote();

            this.isNewNoteOpened = true;
            // Cell is highlighted for easy editing
            eGridCell.classList.add("isNoteEditing");

            // When the user clicks out of the text box, the note closes
            this.noteElement.addEventListener("blur", this.handleNoteBlur);
        }
    }

    public destroy() {
        this.removeEventListeners();
    }
}
